import { Divider, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { PreferenceOption } from '@repo/common/constants/integration/integrations.preferences';
import { Switch } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { IIntegration } from '../../api';
import {
	integrationsQueryKeyFactory,
	queryClient,
	useIntegration,
	useUpdateIntegration,
} from '../../api';

interface PreferenceSwitchProps {
	option: PreferenceOption;
	integration: IIntegration;
	onChange: (fieldName: string, checked: boolean) => void;
}

export function PreferenceSwitch({
	option,
	integration,
	onChange,
}: PreferenceSwitchProps) {
	const getCheckedValue = () => {
		if (option.credentialFieldName) {
			return Boolean(integration?.credentials?.[option.credentialFieldName]);
		}

		if (
			option.integrationFieldName &&
			integration?.[option.integrationFieldName as keyof IIntegration]
		) {
			return Boolean(
				(integration as unknown as Record<string, unknown>)[
					option.integrationFieldName
				]
			);
		}

		return false;
	};

	return (
		<Switch
			key={option.credentialFieldName ?? option.integrationFieldName}
			label={option.title}
			description={option.description}
			onChange={(e) => {
				const fieldName =
					option.credentialFieldName ?? option.integrationFieldName;
				if (fieldName) {
					onChange(fieldName, e.target.checked);
				}
			}}
			checked={getCheckedValue()}
			styles={{
				body: {
					alignItems: 'center',
				},
			}}
		/>
	);
}

interface IIntegrationPreferencesProps {
	id: string;
	preferenceOptions: PreferenceOption[];
}

export const IntegrationPreferences = observer(
	({ id, preferenceOptions }: IIntegrationPreferencesProps) => {
		const { data: integration } = useIntegration({
			id,
			options: {
				enabled: !!id,
			},
		});

		const { mutateAsync } = useUpdateIntegration({
			options: {
				onSuccess() {
					queryClient.invalidateQueries({
						queryKey: integrationsQueryKeyFactory.byId(id),
					});
					showNotification({
						title: 'Preference saved',
						message: 'Run a sync to update resources.',
						color: 'teal',
					});
				},
			},
		});

		const handlePreferenceChange = (fieldName: string, checked: boolean) => {
			if (fieldName in (integration?.credentials ?? {})) {
				const credentials = {
					...(integration?.credentials ?? {}),
					[fieldName]: checked,
				};
				mutateAsync({ data: { id, credentials } });
			} else {
				mutateAsync({
					data: {
						id,
						[fieldName]: checked,
					},
				});
			}
		};

		return (
			<Stack spacing={'xs'}>
				{preferenceOptions.map((option, index) => (
					<>
						<PreferenceSwitch
							key={option.title}
							option={option}
							integration={integration!}
							onChange={handlePreferenceChange}
						/>
						{index < preferenceOptions.length - 1 && <Divider />}
					</>
				))}
			</Stack>
		);
	}
);
